/**
 * Text entry for a whiteboard sticky note: a small floating card with the world-space
 * keyboard folded out beneath. Enter saves, Escape discards.
 */
import type * as THREE from 'three';
import { WHITEBOARD } from '@clinspace/shared';
import type { CanvasPanel, Region } from '../dashboards/CanvasPanel.ts';
import { PALETTE, chip, fillRoundRect, text, panelBackground } from '../dashboards/charts.ts';
import { FloatingPanel, type FloatingPanelSounds } from '../queries/FloatingPanel.ts';
import { drawTextField } from '../queries/textField.ts';
import type { InteractiveSystem } from '../ui/Interactive.ts';

export interface NoteDraft {
  /** Existing note id when editing. */
  id?: string;
  u: number;
  v: number;
  colour: string;
}

type NoteRegion =
  { kind: 'save' } | { kind: 'cancel' } | { kind: 'field' } | { kind: 'colour'; colour: string };

export class NoteEditor extends FloatingPanel {
  draft: NoteDraft | undefined;

  constructor(
    interactive: InteractiveSystem,
    private readonly handlers: {
      save(draft: NoteDraft, text: string): void;
    },
    sounds?: FloatingPanelSounds,
  ) {
    super(
      interactive,
      { width: 0.7, height: 0.42, pixelWidth: 560, pixelHeight: 336, name: 'note-editor' },
      sounds,
    );
  }

  open(draft: NoteDraft, initial: string, at: THREE.Vector3, viewer: THREE.Vector3): void {
    this.draft = draft;
    this.field.clear();
    this.field.value = initial;
    this.place(at, viewer, 0.5);
    this.show();
    this.setTyping(true);
  }

  protected onEnter(): void {
    const t = this.field.trimmed;
    if (!this.draft || !t) return;
    this.handlers.save(this.draft, t.slice(0, 140));
    this.sounds?.click();
    this.hide();
  }

  protected onEscape(): void {
    this.hide();
  }

  protected draw(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const W = panel.pxW;
    const H = panel.pxH;
    panelBackground(ctx, W, H, 16);
    text(ctx, this.draft?.id ? 'Edit sticky note' : 'New sticky note', 20, 36, {
      size: 17,
      weight: 700,
    });
    text(ctx, 'Enter saves, Escape discards. Up to 140 characters.', 20, 60, {
      size: 11,
      colour: PALETTE.muted,
    });
    const field = { x: 20, y: 78, w: W - 40, h: 150 };
    drawTextField(ctx, field, this.field, {
      placeholder: 'Type the note…',
      active: this.typing,
      size: 14,
    });
    panel.region<NoteRegion>({ id: 'field', ...field, data: { kind: 'field' } });
    // Colour swatches.
    let x = 20;
    for (const colour of WHITEBOARD.noteColours) {
      const r = { x, y: 248, w: 44, h: 32 };
      fillRoundRect(ctx, r, 6, colour);
      if (this.draft?.colour === colour) {
        ctx.strokeStyle = PALETTE.teal;
        ctx.lineWidth = 3;
        ctx.strokeRect(r.x - 2, r.y - 2, r.w + 4, r.h + 4);
      }
      panel.region<NoteRegion>({ id: `colour:${colour}`, ...r, data: { kind: 'colour', colour } });
      x += 54;
    }
    const save = chip(ctx, 'Save', W - 180, 250, {
      active: true,
      hovered: panel.isHovered('save'),
      h: 30,
      size: 12,
      minW: 76,
    });
    panel.region<NoteRegion>({ id: 'save', ...save, data: { kind: 'save' } });
    const cancel = chip(ctx, 'Cancel', W - 96, 250, {
      hovered: panel.isHovered('cancel'),
      h: 30,
      size: 12,
      minW: 76,
    });
    panel.region<NoteRegion>({ id: 'cancel', ...cancel, data: { kind: 'cancel' } });
  }

  protected select(region: Region): void {
    const data = region.data as NoteRegion | undefined;
    if (!data) return;
    switch (data.kind) {
      case 'save':
        this.onEnter();
        break;
      case 'cancel':
        this.hide();
        break;
      case 'field':
        this.setTyping(true);
        break;
      case 'colour':
        if (this.draft) this.draft.colour = data.colour;
        this.panel.invalidate();
        break;
    }
  }
}
